import type { FlagDefinition } from "./feature-flag-schema";

/**
 * Every feature flag registered in the application, with its lifecycle
 * metadata. This is the source of truth: `FEATURE_FLAGS`,
 * `SYSTEM_FEATURE_FLAGS` and `FeatureFlagConfig` are derived from it.
 *
 * Adding a flag: pick a `kind`, set `owner` to your GitHub handle, and for a
 * `release`/`experiment` give it `rampBy` ≤ 90 days and `removeBy` ≤ 180 days
 * after `createdOn`. `evaluationRuntime` must match where the flag is read
 * (backend/src → "server", frontend/src → "client", both → "all"); CI checks
 * it. See docs/platform/feature-flag-lifecycle.md.
 *
 * Only a type-check happens here. The Zod parse runs in CI, not at import
 * time, so a bad entry can never white-screen the browser bundle.
 */
export const FEATURE_FLAG_DEFINITIONS = {
  "workflow-expression-resolution-enabled": {
    kind: "release",
    description:
      "Workflow runs feed step handlers from socket expressions instead of stored edges. Bucketed per workflow (distinct id `workflow-expression:<workflowId>`, with organizationId as a property), so a percentage rollout moves whole workflows and an organization condition scopes it. Off keeps edges primary; expressions still run as a dual-read comparison wherever they exist. Read once per run when the definition loads.",
    owner: "Eric162",
    area: "workflows",
    evaluationRuntime: "server",
    createdOn: "2026-10-29",
    rampBy: "2026-12-15",
    removeBy: "2027-03-15",
  },
  "email-ingest-shared-rollout-enabled": {
    kind: "release",
    description:
      "Routes the rollout percentage of an organization's Microsoft Graph email to the shared ingestion engine, evaluated per message. Every legacy message is shadowed without it; existing shared ownership survives disabling.",
    owner: "Eric162",
    area: "platform",
    evaluationRuntime: "server",
    createdOn: "2026-10-28",
    rampBy: "2026-12-15",
    removeBy: "2027-03-15",
  },
  "test-flag": {
    kind: "internal",
    description:
      "Sanity-check flag shown at the top of the /ff_overrides page.",
    owner: "Eric162",
    area: "platform",
    evaluationRuntime: "client",
    createdOn: "2025-10-01",
  },
  "procurement-demo-order-numbers": {
    kind: "internal",
    description:
      "Demo only: fills missing release, PO and transfer numbers on the replenishment orders and transfer order status pages with made-up ones, so the order-number columns can be shown before the ERP's acknowledgements are ingested. The numbers exist nowhere else; keep it off for buyers.",
    owner: "ubenzer",
    area: "procurement",
    ticket:
      "https://linear.app/gently-ai/issue/GEN-3143/2026-10-16-feedback-po-and-release-number-labels-name-as400",
    evaluationRuntime: "client",
    createdOn: "2026-10-30",
  },
  "associate-product-v2-enabled": {
    kind: "release",
    description: "Second version of the associate-product flow.",
    owner: "Eric162",
    area: "oms",
    evaluationRuntime: "client",
    createdOn: "2025-10-01",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "show-email-po-metrics-enabled": {
    kind: "release",
    description: "Email purchase-order metrics page and its route.",
    owner: "Eric162",
    area: "oms",
    evaluationRuntime: "client",
    createdOn: "2025-10-01",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "location-fuzzy-matching-enabled": {
    kind: "release",
    description: "New fuzzy location matching instead of the old matcher.",
    // Introduced by willy-olyslager, who is no longer in the roster.
    owner: "Eric162",
    area: "oms",
    evaluationRuntime: "server",
    createdOn: "2026-01-05",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "sales-order-drafts-ebs-org-filter-enabled": {
    kind: "release",
    description: "EBS organization filter on the sales-order drafts table.",
    // Introduced by willy-olyslager, who is no longer in the roster.
    owner: "Eric162",
    area: "oms",
    evaluationRuntime: "client",
    createdOn: "2026-01-09",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "activity-log-enabled": {
    kind: "release",
    description: "Activity log UI on entity pages.",
    owner: "Eric162",
    area: "platform",
    evaluationRuntime: "client",
    createdOn: "2026-01-10",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "one-to-one-product-variant-mapping-enabled": {
    kind: "release",
    description: "One-to-one customer product to variant mapping UX.",
    // Introduced by willy-olyslager, who is no longer in the roster.
    owner: "Eric162",
    area: "oms",
    evaluationRuntime: "client",
    createdOn: "2026-02-24",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "msft-office-viewer-enabled": {
    kind: "release",
    description: "Microsoft Office Online viewer for documents.",
    owner: "Eric162",
    area: "platform",
    evaluationRuntime: "all",
    createdOn: "2026-03-25",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "chat-agent-enabled": {
    kind: "release",
    description: "Chat agent in the app shell and its backend endpoints.",
    owner: "Eric162",
    area: "agents",
    evaluationRuntime: "all",
    createdOn: "2026-04-13",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "agent-harness-enabled": {
    kind: "release",
    description:
      "Org Settings › Agent Harness sections and the draft review Agent rail tab.",
    owner: "sheldon-gently",
    area: "agents",
    evaluationRuntime: "all",
    createdOn: "2026-09-29",
    rampBy: "2026-12-15",
    removeBy: "2027-03-15",
  },
  "agent-harness-writes-enabled": {
    kind: "kill-switch",
    description:
      "Lets the assistant perform write actions (edit, approve, apply previews, undo) on OMS pages. Off = read-only harness; previews cannot be applied.",
    owner: "sheldon-gently",
    area: "agents",
    evaluationRuntime: "server",
    createdOn: "2026-09-30",
    reviewBy: "2027-03-30",
  },
  "memory-learning-enabled": {
    kind: "release",
    description:
      "Org Settings › Knowledge: review learned customer suggestions and stored memories without the agent harness.",
    owner: "romarioraffington",
    area: "agents",
    evaluationRuntime: "client",
    createdOn: "2026-10-03",
    rampBy: "2026-12-31",
    removeBy: "2027-03-31",
  },
  "nami-copilot-enabled": {
    kind: "release",
    description:
      "Persistent bottom-right Nami button and the always-available docked chat panel on every authenticated page.",
    owner: "sheldon-gently",
    area: "agents",
    evaluationRuntime: "client",
    createdOn: "2026-09-29",
    rampBy: "2026-12-28",
    removeBy: "2027-03-28",
  },
  "orpc-batching-enabled": {
    kind: "release",
    description: "Batch oRPC GET requests from the frontend client link.",
    owner: "Eric162",
    area: "platform",
    evaluationRuntime: "client",
    createdOn: "2026-04-14",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "item-master-ui-enabled": {
    kind: "release",
    description: "Item Master module UI.",
    owner: "dlam",
    area: "inventory",
    evaluationRuntime: "client",
    createdOn: "2026-05-06",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "rebate-two-stage-extraction-enabled": {
    kind: "release",
    description: "Two-stage LLM extraction for rebate agreements.",
    owner: "siddd-bot",
    area: "rebates",
    scopes: ["system"],
    evaluationRuntime: "server",
    createdOn: "2026-05-12",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "inbox-enabled": {
    kind: "release",
    description: "Inbox module: notifications, tasks and agents tabs.",
    owner: "Eric162",
    area: "tasks",
    evaluationRuntime: "all",
    createdOn: "2026-05-14",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "tasks-enabled": {
    kind: "release",
    description:
      "Gates the Tasks queue, task detail, task pages, and task-type/page settings. Independent of inbox-enabled (inbox notifications/UI).",
    owner: "aybmab",
    area: "tasks",
    evaluationRuntime: "client",
    createdOn: "2026-08-25",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "task-outcome-fields-enabled": {
    kind: "release",
    description: "Structured outcome fields on task types.",
    owner: "kylebannerman-gently",
    area: "tasks",
    evaluationRuntime: "all",
    createdOn: "2026-09-20",
    rampBy: "2026-11-19",
    removeBy: "2027-01-18",
  },
  "data-sources-enabled": {
    kind: "release",
    description: "Data Sources page under Organization Settings > Data Model.",
    owner: "dlam",
    area: "platform",
    evaluationRuntime: "client",
    createdOn: "2026-05-19",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "resource-privilege-enforcement-enabled": {
    kind: "release",
    description:
      "Gates ABAC resource-privilege enforcement per organization. When off, privilege filters are logged but not applied. Falls back to the RESOURCE_PRIVILEGE_ENFORCEMENT_ENABLED env var when PostHog is unavailable.",
    owner: "npeaks-gently",
    area: "authz",
    evaluationRuntime: "server",
    createdOn: "2026-06-18",
    rampBy: "2026-10-31",
    removeBy: "2026-12-31",
  },
  "hide-feature-signups": {
    kind: "kill-switch",
    description:
      "Kill switch for the Agents / Tasks / Workflows growth teaser tabs. When ON, signup-only header entries are hidden and Workflows / Agents act as they did before the teaser pages shipped. Absent/OFF keeps the teaser tabs (the default since launch).",
    owner: "aybmab",
    area: "growth",
    evaluationRuntime: "client",
    createdOn: "2026-08-05",
    reviewBy: "2027-03-31",
  },
  "projects-enabled": {
    kind: "release",
    description: "Gently Projects proof of concept.",
    owner: "kylebannerman-gently",
    area: "projects",
    evaluationRuntime: "all",
    createdOn: "2026-09-05",
    rampBy: "2026-11-04",
    removeBy: "2027-01-03",
  },
  "edi-guidelines-enabled": {
    kind: "release",
    description:
      "Gates the organization-settings EDI section holding vendor EDI guidelines. Deliberately a flag rather than the ORDERFUL_EDI module: the guidelines are inbound supplier documents and an organization can hold them without using Orderful at all.",
    owner: "adolfoportillagently",
    area: "edi",
    evaluationRuntime: "all",
    createdOn: "2026-09-09",
    rampBy: "2026-11-08",
    removeBy: "2027-01-07",
  },
  "feedback-triage-enabled": {
    kind: "release",
    description:
      'Persisted feedback: the docked "Send feedback" panel that stores reports for the /feedback-triage dashboard. Off keeps the original modal that only emails support, and hides the dashboard route and menu item.',
    owner: "sheldon-gently",
    area: "feedback",
    evaluationRuntime: "client",
    createdOn: "2026-09-24",
    rampBy: "2026-11-23",
    removeBy: "2027-01-22",
  },
  "ar-tasks-enabled": {
    kind: "release",
    description:
      "AR Tasks page (Accounts Receivable → Tasks) and the billing-task detector that runs after AR and sales-load syncs. Checked alongside the AR module; off hides the page, refuses the API and opens no tasks. Kill switch for the staged Pennrose rollout.",
    owner: "romarioraffington",
    area: "ar",
    ticket: "https://linear.app/gently-ai/issue/GEN-3060",
    evaluationRuntime: "all",
    createdOn: "2026-10-14",
    rampBy: "2026-12-13",
    removeBy: "2027-02-11",
  },
  "ar-cash-dashboard-enabled": {
    kind: "release",
    description:
      "Customizable AR Cash Dashboard (Accounts Receivable → Cash Dashboard), built from the cash metrics template. Off hides the nav entry and the route; the static Cash Metrics page is unaffected.",
    owner: "npeaks-gently",
    area: "ar",
    ticket: "https://linear.app/gently-ai/issue/GEN-3128",
    evaluationRuntime: "client",
    createdOn: "2026-10-16",
    rampBy: "2027-01-14",
    removeBy: "2027-04-14",
  },
  "socket-expression-builder-enabled": {
    kind: "release",
    description:
      "Visual socket expression builder in the workflow editor: clicking an input socket or an expression badge opens the builder docked under the canvas, and sockets with an expression show it as a chip row. Off leaves the canvas read-only for expressions (badges and wires still render).",
    owner: "Eric162",
    area: "workflows",
    evaluationRuntime: "client",
    createdOn: "2026-10-22",
    rampBy: "2026-12-21",
    removeBy: "2027-04-20",
  },
} as const satisfies Record<string, FlagDefinition>;

type FlagDefinitions = typeof FEATURE_FLAG_DEFINITIONS;

export type FeatureFlagKey = keyof FlagDefinitions;

/**
 * Flag keys as a `key → key` map, kept for existing `FEATURE_FLAGS["x"]` call
 * sites.
 */
export const FEATURE_FLAGS = Object.fromEntries(
  Object.keys(FEATURE_FLAG_DEFINITIONS).map((key) => [key, key]),
) as { readonly [K in FeatureFlagKey]: K };

export type SystemFeatureFlagKey = {
  [K in FeatureFlagKey]: FlagDefinitions[K] extends {
    readonly scopes: readonly (infer S)[];
  }
    ? "system" extends S
      ? K
      : never
    : never;
}[FeatureFlagKey];

/**
 * System-level feature flags evaluated against the service identity
 * rather than individual users (`scopes` includes "system"). These flags
 * control backend infrastructure behavior.
 */
export const SYSTEM_FEATURE_FLAGS = Object.fromEntries(
  Object.entries(FEATURE_FLAG_DEFINITIONS)
    .filter(([, def]) => "scopes" in def && def.scopes.includes("system"))
    .map(([key]) => [key, key]),
) as { readonly [K in SystemFeatureFlagKey]: K };

/** Payload type for each feature flag. All flags are boolean today. */
export type FeatureFlagConfig = { [K in FeatureFlagKey]: boolean };

// Generic type for feature flag information
export type FlagInfo<Key extends FeatureFlagKey> = {
  enabled: boolean;
  variantKey?: string;
  payload?: FeatureFlagConfig[Key];
};
